import { Action, State, StateContext, Store } from "@ngxs/store";
import { School } from "../models/school.model";
import { Injectable } from "@angular/core";
import { LocalizationService } from "src/app/services/localization/localization.service";
import { AlertService } from "src/app/shared/store/alert/alert.service";
import { SchoolActions } from "./school.actions";
import { SchoolService } from "../services/school.service";
import { patch, removeItem } from "@ngxs/store/operators";
import { SchoolFormFilter } from "../models/school-form-filter.model";
import { SchoolAddModel } from "../models/school-add-model";
import { SchoolResponse } from "../models/school-response.model";
import { SchoolManagement, SchoolManagementDetails, SchoolManagementRequest } from "../models/school-management.model";
import { SchoolMonitorResponse } from "../models/school-monitor.model";
import { MappingUtilsService } from "src/app/services/utils/mapping-utils.service";
import { FormArray, FormGroup } from "@angular/forms";
import { EngagementService } from "../../engagement/services/engagement.service";
import * as moment from 'moment';

export class SchoolStateModel {
    public schoolsMap: School[];
    public schools: SchoolResponse[];
    public monitorsSchools: SchoolMonitorResponse[];
    schoolForm: {
      model: SchoolManagementDetails
    };
    schoolAddForm: {
      model: SchoolAddModel
    }
    filters: {
      model: SchoolFormFilter;
    };
    public isLoadingSchools: boolean;
    public isListSmaller: boolean;
    public isSubmitted: boolean;
    public isEdited: boolean;
    public error: string;
  }

  const defaults = {
    schoolsMap: null,
    schools: null,
    monitorsSchools: null,
    schoolForm: {
      model: null
    },
    schoolAddForm: {
      model: null
    },
    filters: {
      model: null,
    },
    isLoadingSchools: false,
    isLoadingSchool: false,
    isSubmitted: false,
    isListSmaller: true,
    isEdited: false,
    error: null
  };

  @State<SchoolStateModel>({
    name: 'school',
    defaults,
  })
  @Injectable()
  export class SchoolState {
    constructor(
      private alertService: AlertService,
      private localizationService: LocalizationService,
      private schoolService: SchoolService,
      private engagementService: EngagementService,
      public store: Store
    ) {}

  @Action(SchoolActions.GetSchoolsMap) GetSchoolsMap (ctx: StateContext<SchoolStateModel>, { zoneId, profitCenterCode, exerciseId, user, contractType }: SchoolActions.GetSchoolsMap) {
    return this.schoolService.getSchoolsMap(zoneId, profitCenterCode, exerciseId, user, contractType)
      .subscribe({
        next: (response) =>
          ctx.dispatch(new SchoolActions.GetSchoolsMapSuccess(response)),
        error: (error) =>
          ctx.dispatch(new SchoolActions.Error(error)),
      });
  }

  @Action(SchoolActions.GetSchoolsMapSuccess)
  getSchoolMapSuccess(ctx: StateContext<SchoolStateModel>, { schoolsMap }: SchoolActions.GetSchoolsMapSuccess) {
    ctx.patchState({
      schoolsMap: schoolsMap.sort((a, b) => a.description.localeCompare(b.description)),
      isLoadingSchools: false,
    });
  }

  @Action(SchoolActions.GetSchools)
  getSchools (ctx: StateContext<SchoolStateModel>, { zoneId, profitCenterCode, exerciseId, user }: SchoolActions.GetSchools) {
    ctx.patchState({ isLoadingSchools: true });
    return this.schoolService.getSchools(zoneId, profitCenterCode, exerciseId, user)
      .subscribe({
        next: (response) =>
          ctx.dispatch(new SchoolActions.GetSchoolsSuccess(response)),
        error: (error) =>
          ctx.dispatch(new SchoolActions.Error(error)),
      });
  }

  @Action(SchoolActions.GetSchoolsSuccess)
  getSchoolSuccess(ctx: StateContext<SchoolStateModel>, { schools }: SchoolActions.GetSchoolsSuccess) {
    ctx.patchState({
      schools: schools,
      isLoadingSchools: false,
    });
  }

  @Action(SchoolActions.GetSchoolDetails)
  getSchoolDetails (ctx: StateContext<SchoolStateModel>, { schoolId, exerciseId }: SchoolActions.GetSchoolDetails) {
    ctx.patchState({ isLoadingSchools: true });
    return this.schoolService.getSchoolDetails(schoolId, exerciseId)
      .subscribe({
        next: (response) =>
          ctx.dispatch(new SchoolActions.GetSchoolDetailsSuccess(response)),
        error: (error) =>
          ctx.dispatch(new SchoolActions.Error(error)),
      });
  }

  @Action(SchoolActions.GetSchoolDetailsSuccess)
  getSchoolDetailsSuccess(ctx: StateContext<SchoolStateModel>, { school }: SchoolActions.GetSchoolDetailsSuccess) {
    const schoolManagementRequest = this.getSchoolManagementRequestModel(school);
    ctx.patchState({
      schoolForm: { model: schoolManagementRequest },
      isLoadingSchools: false
    });
  }

  getSchoolManagementRequestModel(school: SchoolManagement): SchoolManagementDetails {
    let schoolManagementRequest = new SchoolManagementDetails();
    schoolManagementRequest.schoolId = school.school.schoolId;
    schoolManagementRequest.schoolName = school.school.schoolName;
    schoolManagementRequest.centerCode = school.school.centerCode;
    schoolManagementRequest.schoolNumber = school.school.schoolNumber;
    schoolManagementRequest.batch = school.school.batch;
    schoolManagementRequest.adpAcceptCenter = school.school.adpAcceptCenter;
    schoolManagementRequest.invoiceAccount = school.school.invoiceAccount;
    schoolManagementRequest.languageId = school.school.languageId;
    schoolManagementRequest.schoolAccountingAccount = school.school.schoolAccountingAccount;
    schoolManagementRequest.zoneId = school.school.zoneId;
    schoolManagementRequest.profitCenterCode = school.school.profitCenterCode;
    schoolManagementRequest.centerType = school.school.centerType;
    schoolManagementRequest.serviceType = school.school.serviceType;

    schoolManagementRequest.paymentTerms = school.school.paymentTerms;
    schoolManagementRequest.paymentMethod = school.school.paymentMethod;
    schoolManagementRequest.cauCode = school.school.cauCode;
    schoolManagementRequest.cauName = school.school.cauName;
    schoolManagementRequest.isPrivate = school.school.isPrivate;
    schoolManagementRequest.invoicingMail = school.school.invoicingMail;
    schoolManagementRequest.managerId = school.school.managerId;
    schoolManagementRequest.identification = school.school.identification;
    schoolManagementRequest.address = school.school.address;
    schoolManagementRequest.city = school.school.city;
    schoolManagementRequest.zipCode = school.school.zipCode;
    schoolManagementRequest.province = school.school.province;
    schoolManagementRequest.iseMonSchoolCode = school.school.iseMonSchoolCode;
    schoolManagementRequest.showInReport = school.school.showInReport;
    schoolManagementRequest.manager = school.school.manager;
    schoolManagementRequest.schoolServices = school.schoolServices;
    schoolManagementRequest.historicSchoolServices = school.historicSchoolServices.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

    return schoolManagementRequest;
  }

  @Action(SchoolActions.GetSchoolsForMonitor)
  getSchoolsForMonitor (ctx: StateContext<SchoolStateModel>, { exerciseId, user }: SchoolActions.GetSchoolsForMonitor) {
    ctx.patchState({ isLoadingSchools: true });
    return this.schoolService.getSchoolsForMonitors(exerciseId, user)
      .subscribe({
        next: (response) =>
          ctx.dispatch(new SchoolActions.GetSchoolsForMonitorSuccess(response)),
        error: (error) =>
          ctx.dispatch(new SchoolActions.Error(error)),
      });
  }

  @Action(SchoolActions.GetSchoolsForMonitorSuccess)
  getSchoolsForMonitorSuccess(ctx: StateContext<SchoolStateModel>, { response }: SchoolActions.GetSchoolsForMonitorSuccess) {
    ctx.patchState({
      monitorsSchools: response,
      isLoadingSchools: false,
    });
  }

  @Action(SchoolActions.AddSchool)
  addSchool(ctx: StateContext<SchoolStateModel>, {school, user}: SchoolActions.AddSchool) {
    this.alertService.confirm(
    this.localizationService.translate("schools.school"),
    this.localizationService.translate('masterForms.schoolAddConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('masterForms.schoolAddConfirm')
    .replace('{0}', school.schoolName)
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoadingSchools: true});
        return this.schoolService.createSchool(school, user).subscribe({
          next: (newSchoolId) => {
            ctx.dispatch(new SchoolActions.AddSchoolSuccess(school, newSchoolId))
          },
          error: (error) => {
            ctx.dispatch(new SchoolActions.Error(error));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(SchoolActions.AddSchoolSuccess)
  addSchoolSuccess(ctx: StateContext<SchoolStateModel>, {school, newSchoolId}: SchoolActions.AddSchoolSuccess) {
    const state = ctx.getState();
    ctx.patchState({
      ...state.schools, ...school,
      isLoadingSchools: false,
    });
    return newSchoolId
  }

  @Action(SchoolActions.SetSchoolsDefaultState)
  setSchoolsDefaultState(ctx: StateContext<SchoolStateModel>, {}: SchoolActions.SetSchoolsDefaultState) {
    ctx.patchState(
      defaults
    );
  }

  @Action(SchoolActions.SetSchoolAddFormDefaultState)
  setSchoolsAddFormDefaultState(ctx: StateContext<SchoolStateModel>, {}: SchoolActions.SetSchoolAddFormDefaultState) {
    ctx.setState(
      patch({
        schoolAddForm: patch({
          model: null
        }),
      })
    );
  }

  @Action(SchoolActions.SetSchoolFormDefaultState)
  setSchoolsFormDefaultState(ctx: StateContext<SchoolStateModel>, {}: SchoolActions.SetSchoolFormDefaultState) {
    ctx.setState(
      patch({
        schoolForm: patch({
          model: null
        }),
      })
    );
  }

  @Action(SchoolActions.EditSchool)
  editSchool(ctx: StateContext<SchoolStateModel>, {schoolForm, user}: SchoolActions.EditSchool) {
    this.alertService.confirm(
      this.localizationService.translate("labels.school"),
      this.localizationService.translate('masterForms.schoolEditConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('masterForms.schoolEditConfirm')
      .replace('{0}', schoolForm.get("schoolName").value)
    ).then((result) => {
      if (result.value) {
        const schoolEditModel = this.getSchoolEditModel(schoolForm);
        ctx.patchState({isLoadingSchools: true});
        return this.schoolService.updateSchool(schoolEditModel, user).subscribe({
          next: (res) => ctx.dispatch(new SchoolActions.EditSchoolSuccess(schoolForm)),
          error: (error) => {
            ctx.dispatch(new SchoolActions.Error(error));
          }
        });
      } else {
        return null;
      }
    });
  }

  getSchoolEditModel(schoolForm: FormGroup<any>) {
    const schoolManagementRequest = {
      schoolId: schoolForm.get("schoolId").value,
      schoolName: schoolForm.get("schoolName").value,
      centerCode: schoolForm.get("centerCode").value,
      schoolNumber: schoolForm.get("schoolNumber").value,
      batch: schoolForm.get("batch").value,
      adpAcceptCenter: schoolForm.get("adpAcceptCenter").value,
      invoiceAccount: schoolForm.get("invoiceAccount").value,
      languageId: schoolForm.get("languageId").value,
      schoolAccountingAccount: schoolForm.get("schoolAccountingAccount").value,
      zoneId: schoolForm.get("zoneId").value,
      profitCenterCode: schoolForm.get("profitCenterCode").value,
      centerType: schoolForm.get("centerType").value,
      serviceType: schoolForm.get("serviceType").value,
      customPayment: schoolForm.get("customPayment").value,
      paymentTerms: schoolForm.get("paymentTerms").value,
      paymentMethod: schoolForm.get("paymentMethod").value,
      cauCode: schoolForm.get("cauCode").value,
      cauName: schoolForm.get("cauName").value,
      isPrivate: schoolForm.get("isPrivate").value,
      invoicingMail: schoolForm.get("invoicingMail").value,
      managerId: schoolForm.get("managerId").value,
      identification: schoolForm.get("identification").value,
      address: schoolForm.get("address").value,
      city: schoolForm.get("city").value,
      zipCode: schoolForm.get("zipCode").value,
      province: schoolForm.get("province").value,
      iseMonSchoolCode: schoolForm.get("iseMonSchoolCode").value,
      showInReport: schoolForm.get("showInReport").value,
      manager: schoolForm.get("manager").value,
      schoolServices: this.createSchoolServices(schoolForm.get("schoolServices") as FormArray<FormGroup>)
    }

    return schoolManagementRequest;
  }

  createSchoolServices(schoolServicesFormArray: FormArray<FormGroup>) {
    let schoolServices = [];
    if(!schoolServicesFormArray) return schoolServices;
    schoolServicesFormArray.controls.filter(x => x.dirty).forEach((schoolService: FormGroup) => {
      let schoolServiceData = {
        schoolEngagementId: schoolService.get("schoolEngagementId").value,
        schoolId: schoolService.get("schoolId").value,
        serviceId: schoolService.get("serviceId").value,
        startDate: moment(schoolService.get("startDate").value).format('YYYY-MM-DD'),
        endDate: moment(schoolService.get("endDate").value).format('YYYY-MM-DD'),
        monday: schoolService.get("monday").value,
        tuesday: schoolService.get("tuesday").value,
        wednesday: schoolService.get("wednesday").value,
        thursday: schoolService.get("thursday").value,
        friday: schoolService.get("friday").value,
        saturday: schoolService.get("saturday").value,
        sunday: schoolService.get("sunday").value,
        contractId: schoolService.get("contractId").value,
      };
      schoolServices.push(schoolServiceData);
    });
    return schoolServices;
  }

  @Action(SchoolActions.EditSchoolSuccess)
  editSchoolSuccess(ctx: StateContext<SchoolStateModel>, {schoolForm}: SchoolActions.EditSchoolSuccess) {
    ctx.setState(
      patch({
        isLoadingSchools: false
      })
    );
  }

  @Action(SchoolActions.DeleteSchoolService)
  deleteSchoolService(ctx: StateContext<SchoolStateModel>, {schoolEngagementId, schoolId}: SchoolActions.DeleteSchoolService) {
    this.alertService.confirm(
      this.localizationService.translate("labels.school"),
      this.localizationService.translate('school-services.schoolServiceDeleteConfirm'),
      this.localizationService.translate('buttons.accept'),
      this.localizationService.translate('buttons.cancel'),
      this.localizationService.translate('school-services.schoolServiceDeleteConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoadingSchools: true});
        return this.engagementService.deleteSchoolService(schoolEngagementId, schoolId).subscribe({
          next: (res) => ctx.dispatch(new SchoolActions.DeleteSchoolServiceSuccess(schoolEngagementId)),
          error: (error) => {
            ctx.dispatch(new SchoolActions.Error(error));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(SchoolActions.DeleteSchoolServiceSuccess)
  deleteSchoolServiceSuccess(ctx: StateContext<SchoolStateModel>, {schoolEngagementId}: SchoolActions.DeleteSchoolServiceSuccess) {
    ctx.setState(
      patch({
        schoolForm: patch({
          model: patch({
            schoolServices: removeItem<any>(
              (schoolService) => schoolService.schoolEngagementId === schoolEngagementId
            )
          })
        })
      })
    );
    ctx.patchState({isLoadingSchools: false});
  }

  @Action(SchoolActions.SetFilterCurrentExercise)
  setFilterCurrentExercise(ctx: StateContext<SchoolStateModel>, {exerciseId}: SchoolActions.SetFilterCurrentExercise) {
    ctx.setState(
      patch({
        filters: patch({
          model: patch({
            exerciseId: exerciseId
          })
        })
      })
    );
  }

  @Action(SchoolActions.SetIsListSmaller)
  setIsListSmaller(
    ctx: StateContext<SchoolStateModel>,
    { isListSmaller }: SchoolActions.SetIsListSmaller
  ) {
    ctx.patchState({
      isListSmaller: isListSmaller,
    });
  }

  @Action(SchoolActions.SetIsEdited)
  setIsEdited(
    ctx: StateContext<SchoolStateModel>,
    { isEdited }: SchoolActions.SetIsEdited
  ) {
    ctx.patchState({
      isEdited,
    });
  }

  @Action(SchoolActions.SetIsSubmitted)
  setIsSubmitted(ctx: StateContext<SchoolStateModel>, { isSubmitted }: SchoolActions.SetIsSubmitted
  ) {
    ctx.patchState({
      isSubmitted: isSubmitted,
    });
  }

  @Action(SchoolActions.Error)
  onErrorOcurred(ctx: StateContext<SchoolStateModel>, { error }: SchoolActions.Error) {
    ctx.patchState({
      error: error,
      isLoadingSchools: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  private getTitle() {
    return this.localizationService.translate('labels.school');
  }
}
